import extend from 'lodash/extend';
import mergeWith from 'lodash/mergeWith';
import { normalize } from 'normalizr';
import { createReducer, current } from '@reduxjs/toolkit';
import moment from 'moment';

import TimezonesService from 'account_settings/services/timezones-service';
import { replaceArrays } from 'shared/lodash-utils';
import { getCurrentUser, getCurrentUserId } from 'redux/selectors/users';
import { MyAccount } from 'redux/schemas/models/my-account';
import { NameAndEmailFormSource } from 'redux/schemas/models/user';
import { UpdateCourseNameAndEmailParams } from 'redux/schemas/api/user';
import { EditableCourseElements } from 'redux/schemas/models/edit-translations';
import { AccordionLectureComponentData, ComponentType, NLectureComponent } from 'redux/schemas/models/lecture-component';
import { Exercise } from 'redux/schemas/models/exercise';
import {
  getMyAccount,
  updateUser,
  setUser,
  getBasicUserInfo,
  updateEnrollment,
  setProfilePicture,
  removeUserEnrollment,
  updateHasViewedRteFte,
  updateBasicUserProfile,
  updateRecentlyUsedColors,
  updateNameAndEmail,
  setCurrentUserEnrollments,
  enrollWithRole,
  translateLecturePage,
  translateOutline,
  unsetTranslationPreference,
  searchCourseUsers,
  searchCourseOrgUsers,
  updateSystemGeneratedEmail,
  setShowUpdateEmailAlert,
  patchUser,
  searchOrgUsersForProgram,
  translateJourney,
  translationUpdateSections,
  getAccountEnrollments,
  getUserConfigTokens,
  updateUserPlatformLanguage,
  translationUpdate,
} from '../actions/users';
import {
  basicUserInfoSchema,
  courseUsersSchema,
  enrollmentSchema,
  enrollmentsSchema,
  myAccount,
  usersListSchema,
} from '../schemas/api/my-account';
import { initialRootState } from './index';

export default createReducer(initialRootState, builder => {
  builder
    .addCase(getMyAccount.fulfilled, (state, action) => {
      const data = normalize(action.payload, myAccount);
      Object.assign(state.models.users, data.entities.myAccount);
      mergeWith(state.models.courses, data.entities.courses, replaceArrays);
      Object.assign(state.models.enrollments, data.entities.enrollments);
      state.app.currentUserId = data.result;
      // Sets the current institution id if not already set
      // Mimics the Angular.js app's assumption that the 1st institution returned from my_account.json
      // is the current one
      if (!state.app.currentInstitutionId && data.entities?.institutions) {
        state.app.currentInstitutionId = Object.values(data.entities.institutions)[0].id;
        state.models.institutions = data.entities.institutions as any;
      }

      if (!state.app.lecturePage.currentLectureId) {
        state.app.lecturePage.leftPanelVisibilityPreference = data.entities.myAccount[state.app.currentUserId].isTimelineExpanded;
      }
    })
    .addCase(setUser, (state, action) => {
      const currentUserid = getCurrentUserId(state);
      state.models.users[currentUserid] = {
        ...state.models.users[currentUserid],
        ...(typeof action.payload === 'object' && action.payload !== null ? action.payload : {}),
      };
    })
    .addCase(updateUser.fulfilled, (state, action) => {
      const currentUserid = getCurrentUserId(state);

      const updatedParams = action.meta.arg;

      // If updatedParams has timezone, the sent value is shortName, but we
      // need to store the longName in the user state.
      if (updatedParams.timeZone) {
        const timezonesService = new TimezonesService(null, moment);
        const timezone = timezonesService.timezones.find((tz) => tz.shortName === updatedParams.timeZone)?.longName;
        if (timezone) {
          updatedParams.timeZone = timezone;
        }
      }

      state.models.users[currentUserid] = {
        ...state.models.users[currentUserid],
        ...updatedParams,
      };
    })
    .addCase(updateUserPlatformLanguage, (state, action) => {
      const currentUserid = getCurrentUserId(state);
      state.models.users[currentUserid].platformLanguage = action.payload.selectedLanguage;
    })
    .addCase(updateHasViewedRteFte.pending, (state, action) => {
      const currentUser = getCurrentUser(state);
      state.models.users[currentUser.id].hasViewedRteFte = true;
    })
    .addCase(updateRecentlyUsedColors.pending, (state, action) => {
      const currentUser = getCurrentUser(state);
      state.models.users[currentUser.id].recentlyUsedColors = action.meta.arg;
    })
    .addCase(getBasicUserInfo.fulfilled, (state, action) => {
      const data = normalize(action.payload, basicUserInfoSchema);

      mergeWith(state.models.courses, data.entities.courses, replaceArrays);
      mergeWith(state.models.users, data.entities.basicUserInfo, replaceArrays);
      mergeWith(state.models.enrollments, data.entities.enrollments, replaceArrays);
      state.app.redirecting = false;
    })
    .addCase(updateEnrollment, (state, action) => {
      extend(state.models.enrollments[action.payload.id], action.payload.patch);
    })
    .addCase(setProfilePicture.fulfilled, (state, action) => {
      const currentUser = getCurrentUser(state);

      currentUser.profilePicture = action.payload;
    })
    .addCase(removeUserEnrollment, (state, action) => {
      const {
        userId,
        enrollmentId: enrollmentIdToRemove,
      } = action.payload;

      delete state.models.enrollments[enrollmentIdToRemove];
      state.models.users[userId].enrollmentIds = state.models.users[userId].enrollmentIds.filter(
        (enrollmentId) => enrollmentId !== enrollmentIdToRemove,
      );
    })
    .addCase(updateBasicUserProfile.fulfilled, (state, action) => {
      const { currentUserId } = state.app;
      const { user } = action.meta.arg;

      /**
       * NOTE: This request currently doesn't respond with the updated user, so
       * for now I am updating redux state with the payload sent.
       */
      Object.assign(state.models.users[currentUserId], {
        ...user,
        initials: getInitials(user),
      });
    })
    .addCase(updateNameAndEmail.fulfilled, (state, action) => {
      const { userId, source } = action.meta.arg;
      const { user } = action.payload;

      if (user && state?.models?.users[userId]) {
        Object.assign(state.models.users[userId], {
          ...user,
          initials: getInitials(user),
        });
      }

      if (source === NameAndEmailFormSource.CONTENT_MANAGEMENT_COLLECTION) {
        const { catalogId, courseUserId } = action.meta.arg as UpdateCourseNameAndEmailParams;
        const course = state.models.courses[catalogId];
        if (course?.isContentManagementCollection) {
          mergeWith(state.models.courseUsers[catalogId][courseUserId].user, user);
        }
      }
    })
    .addCase(setCurrentUserEnrollments, (state, action) => {
      const normalized = normalize(
        action.payload,
        enrollmentsSchema,
      );

      state.models.users[state.app.currentUserId].enrollmentIds = normalized.result;
      mergeWith(state.models, normalized.entities, replaceArrays);
    })
    .addCase(enrollWithRole.fulfilled, (state, action) => {
      const normalized = normalize(action.payload, enrollmentSchema);
      state.models.users[state.app.currentUserId].enrollmentIds.push(normalized.result);
      mergeWith(state.models, normalized.entities, replaceArrays);
    })
    .addCase(translateLecturePage.pending, (state, action) => {
      if (action.meta.arg.lecturePageId === state.app.destinationLecturePage?.id) {
        state.app.destinationLecturePage.translated = false;
      } else {
        state.app.lecturePage.translationFailed = false;
        state.app.lecturePage.isTranslating = true;
      }
    })
    .addCase(translateLecturePage.rejected, (state, action) => {
      if (action.meta.arg.lecturePageId === state.app.destinationLecturePage?.id) {
        state.app.destinationLecturePage.translated = false;
      } else {
        state.app.lecturePage.isTranslating = false;
        state.app.lecturePage.translationFailed = true;
      }
    })
    .addCase(translateLecturePage.fulfilled, (state, action) => {
      if (action.meta.arg.lecturePageId === state.app.destinationLecturePage?.id) {
        state.app.destinationLecturePage.translated = true;
      }
      const currentUser = getCurrentUser(state);
      currentUser.translationPreferenceLanguage = action.meta.arg.language;
      currentUser.lastTranslationPreferenceLanguage = action.meta.arg.language;
    })
    .addCase(translateOutline.fulfilled, (state, action) => {
      const currentUser = getCurrentUser(state);
      currentUser.translationPreferenceLanguage = action.meta.arg.language;
      currentUser.lastTranslationPreferenceLanguage = action.meta.arg.language;
    })
    .addCase(unsetTranslationPreference.pending, (state, action) => {
      if (state.models.courses[state.app.currentCatalogId].isJourney) {
        // It requires a non empty string, not related to any language.
        state.app.upcomingJourneyLanguage = 'NoLanguage';
      }
    })
    .addCase(unsetTranslationPreference.fulfilled, (state) => {
      const currentUser = getCurrentUser(state);
      currentUser.translationPreferenceLanguage = null;
      if (state.app.upcomingJourneyLanguage) {
        state.app.upcomingJourneyLanguage = null;
      }
    })
    .addCase(searchCourseUsers.fulfilled, (state, action: any) => {
      const data = normalize/* <CourseUser, OrgUserEntities> */(
        action.payload.response,
        courseUsersSchema,
      );
      state.app.courseUsersLoaded = true;
      mergeWith(state.models.courseUsers, { [action.meta.arg.catalogId]: data.entities.courseUser }, replaceArrays);
    })
    .addCase(searchCourseOrgUsers.fulfilled, (state, action) => {
      state.app.collection.hasMoreInUsersSearch = action.payload.count > action.meta.arg.page * action.meta.arg.pageSize;
    })
    .addCase(searchOrgUsersForProgram.fulfilled, (state, action) => {
      const normalized = normalize(action.payload, usersListSchema);

      mergeWith(state.models, normalized.entities, replaceArrays);
    })
    .addCase(updateSystemGeneratedEmail.fulfilled, (state, action) => {
      if (!action.payload?.errorCodes?.length) {
        state.models.users[state.app.currentUserId].email = action.meta.arg.user.email;
        state.models.users[state.app.currentUserId].hasSystemGeneratedAddress = false;
      }
    })
    .addCase(setShowUpdateEmailAlert, (state, action) => {
      state.app.showUpdateEmailAlert = action.payload;
    })
    .addCase(patchUser, (state, action) => {
      const { id, ...restUser } = action.payload as Partial<MyAccount>;

      Object.assign(state.models.users[id], restUser);
    })
    .addCase(translateJourney.pending, (state, action) => {
      state.app.upcomingJourneyLanguage = action.meta.arg.language;
    })
    .addCase(translationUpdateSections.fulfilled, (state, action) => {
      if (action.meta.arg.type === EditableCourseElements.LESSON) {
        state.models.lecturePages[action.meta.arg.id] = {
          ...state.models.lecturePages[action.meta.arg.id],
          title: action.meta.arg.attributes[0].text,
          aiEdited: true,
        };
      } else {
        state.models.lectureSections[action.meta.arg.id] = {
          ...state.models.lectureSections[action.meta.arg.id],
          title: action.meta.arg.attributes[0].text,
          aiEdited: true,
        };
      }
    })
    .addCase(translationUpdate.fulfilled, (state, action) => {
      action.meta.arg.attributes.forEach((attribute) => {
        let elements = null;
        let elementIndex = attribute.elementId;
        switch (attribute.elementType) {
          case ComponentType.ACCORDION_SECTION:
            elements = (state.models.lectureComponents[action.meta.arg.id] as AccordionLectureComponentData).accordionSections;
            break;
          case ComponentType.PUBLIC_PEER_EVALUATION:
          case ComponentType.PRIVATE_PEER_EVALUATION:
            elements = state.models.peerEvaluations;
            break;
          case ComponentType.TEAM_DISCUSSION:
          case ComponentType.TOPIC: {
            state.models.posts[attribute.elementId] = {
              ...state.models.posts[attribute.elementId],
              [attribute.name]: attribute.text,
            };
            elementIndex = null;
            const topicTag = 'topic'; // To avoid parsing and type issues when calling ".topic" directly
            state.models.lectureComponents[action.meta.arg.id][topicTag] = {
              ...state.models.lectureComponents[action.meta.arg.id][topicTag],
              [attribute.name]: attribute.text,
            };
            break;
          }
          case ComponentType.EXERCISE:
            if (attribute.name === 'header' || attribute.name === 'instructions') {
              const lectureComponent = state.models.lectureComponents[action.meta.arg.id] as NLectureComponent<ComponentType.EXERCISE>;
              elements = (state.models.exercises[lectureComponent.exercise] as unknown as Exercise).template.sections;
            } else {
              elements = state.models.exercises;
            }
            break;
          case ComponentType.POLL:
            if (attribute.name === 'title') {
              elements = state.models.polls;
            } else if (attribute.name === 'questionText') {
              elements = state.models.pollQuestions;
            } else {
              elements = state.models.pollOptions;
            }
            break;
          case ComponentType.PROFILE_COMPLETION:
            if (attribute.name === 'content') {
              elements = state.models.lectureComponents;
            } else {
              elements = state.models.profileRequirements;
            }
            break;
          case ComponentType.AUDIO:
          case ComponentType.VIDEO:
            if (attribute.name === 'name') {
              state.models.lectureVideos[attribute.elementId].title = attribute.text;
              elementIndex = state.models.lectureVideos[attribute.elementId].video;
              elements = state.models.video;
            } else {
              elements = state.models.lectureComponents;
            }
            break;
          case ComponentType.SURVEY:
            if (attribute.questionId) {
              if (!attribute.optionId) { // Update a question
                elements = state.models.quizQuestions;
                elementIndex = attribute.questionId;
              } else { // Update an option
                elements = state.models.quizQuestionOptions;
                elementIndex = attribute.optionId;
              }
            } else {
              elements = state.models.surveys;
            }
            break;
          case ComponentType.QUIZ:
          case ComponentType.PROGRESSIVE_QUIZ:
            if (attribute.questionId) {
              if (attribute.feedbackId) { // Update a feedback
                elements = state.models.quizQuestionFeedbacks;
                elementIndex = attribute.feedbackId;
              } else if (attribute.optionId) { // Update an option
                elements = state.models.quizQuestionOptions;
                elementIndex = attribute.optionId;
              } else { // Update a question
                elements = state.models.quizQuestions;
                elementIndex = attribute.questionId;
              }
            } else {
              elements = attribute.elementType === ComponentType.QUIZ ? state.models.quizzes : state.models.progressiveQuizzes;
            }
            break;
          case ComponentType.EXTERNAL_TOOL:
            elements = state.models.externalTools;
            if (attribute.name === 'name') {
              state.models.externalTools[attribute.elementId].title = attribute.text;
            }
            break;
          default:
            elementIndex = action.meta.arg.id;
            elements = state.models.lectureComponents;
        }
        if (elementIndex !== null && elements !== null) {
          elements[elementIndex] = {
            ...elements[elementIndex],
            [attribute.name]: attribute.text,
          };
        }
      });

      if (action.meta.arg.attachments?.length > 0) {
        action.meta.arg.attachments.forEach((attachment, index) => {
          const lectureComponent = state.models.lectureComponents[action.meta.arg.id] as NLectureComponent<ComponentType.EXERCISE>;
          const exercise = state.models.exercises[lectureComponent.exercise];
          const currentAttachmentIndex = exercise.attachments.findIndex(currentAttachment => currentAttachment.id === attachment.translatedFrom);
          const payloadAttachment = action.payload[index];
          if (payloadAttachment) {
            exercise.attachments[currentAttachmentIndex] = {
              ...exercise.attachments[currentAttachmentIndex],
              autoTranslated: {
                lectureComponentId: lectureComponent.id,
                attachmentId: payloadAttachment.id,
                boxDocumentId: payloadAttachment.file.boxDocumentId,
                translatedFromId: attachment.translatedFrom,
                documentName: payloadAttachment.file.name,
                documentUrl: payloadAttachment.file.url,
                errorCode: null,
                language: action.meta.arg.language,
                status: 'completed',
                manuallyTranslated: true,
              },
            };
          }
        });
      }
      state.models.lectureComponents[action.meta.arg.id] = {
        ...state.models.lectureComponents[action.meta.arg.id],
        aiEdited: true,
      };
    })
    .addCase(translateJourney.fulfilled, (state, action) => {
      state.app.upcomingJourneyLanguage = null;
      const currentUser = getCurrentUser(state);
      currentUser.translationPreferenceLanguage = action.meta.arg.language;
      currentUser.lastTranslationPreferenceLanguage = action.meta.arg.language;
    })
    .addCase(getAccountEnrollments.pending, (state, action) => {
      state.app.accountEnrollmentsLoaded = false;
    })
    .addCase(getAccountEnrollments.fulfilled, (state, action) => {
      const data = normalize(action.payload, enrollmentsSchema);
      mergeWith(state.models.courses, data.entities.courses, replaceArrays);
      mergeWith(state.models.enrollments, data.entities.enrollments, replaceArrays);
      state.models.users[state.app.currentUserId].enrollmentIds = data.result;

      state.app.accountEnrollmentsLoaded = true;
    })
    .addCase(getUserConfigTokens.fulfilled, (state, action) => {
      state.app.configKey = action.payload;
      state.app.configKey.isLoaded = true;
    });
});

const getInitials = (user) => (user.firstName[0]?.toUpperCase() ?? '')
  + (user.lastName[0]?.toUpperCase() ?? '');
